/**
 * P-14 客户与订单（docs/03-UIUX.md §5）。
 *
 * 列表 + 右侧抽屉：客户列表（搜索/分页/操作），点击行打开抽屉查看详情与订单。
 * 支持：登记客户（POST /admin/customers）、编辑客户（PATCH）、登记订单（POST /admin/orders，source=manual）。
 * 客户为可选实体，未登记客户显示「未登记」；订单列表走 /admin/orders?customer_id=。
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ErrorBlock, EmptyFirstRun } from '../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../components/Pager';
import { describeError } from '../api/client';
import { createCustomer, createOrder, listCustomers, listOrders, listProducts, updateCustomer } from '../api/endpoints';
import type { Customer, CustomerInput, Order } from '../api/types';
import { useAsync } from '../lib/useAsync';
import { formatDate } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';

export function CustomersPage() {
  const { currentUser } = useAuth();
  const canWrite = currentUser?.role === 'owner' || currentUser?.role === 'operator';

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [viewingCustomerId, setViewingCustomerId] = useState<string | null>(null);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [orderingCustomer, setOrderingCustomer] = useState<Customer | null>(null);

  // 搜索防抖 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // 搜索词变化时关闭抽屉（当前客户可能不在结果里）
  useEffect(() => {
    if (debouncedQ !== '') setViewingCustomerId(null);
  }, [debouncedQ]);

  const customers = useAsync(
    useCallback(() => listCustomers({ q: debouncedQ === '' ? undefined : debouncedQ, page, limit }), [debouncedQ, page, limit]),
    [debouncedQ, page, limit],
  );
  const products = useAsync(useCallback(() => listProducts({ limit: 200 }), []), []);

  const customerList: readonly Customer[] = customers.state.kind === 'ready' ? customers.state.data.items : [];
  const total = customers.state.kind === 'ready' ? customers.state.data.total : 0;
  const hasMore = customers.state.kind === 'ready' ? customers.state.data.hasMore : false;
  const viewingCustomer = customerList.find((c) => c.id === viewingCustomerId) ?? null;

  const refreshAll = () => {
    customers.reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">客户与订单</h1>
          <p className="page-head__sub">
            客户列表与其订单。客户为可选实体，未登记客户显示为「未登记」；可手动登记客户与订单。
          </p>
        </div>
        {canWrite && (
          <div className="page-head__actions">
            <button type="button" className="btn btn--primary" onClick={() => setShowCreateCustomer(true)}>
              <Icon name="plus" size={ICON_SIZE.inline} />
              登记客户
            </button>
          </div>
        )}
      </div>

      <div className="field" style={{ marginBottom: 12 }}>
        <div className="input-with-icon">
          <Icon name="search" size={ICON_SIZE.inline} />
          <input
            className="input"
            type="search"
            placeholder="搜索姓名 / 邮箱 / 电话…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            aria-label="搜索客户"
          />
        </div>
      </div>

      {customers.state.kind === 'failed' ? (
        <ErrorBlock message={describeError(customers.state.error, '加载客户列表')} onRetry={customers.reload} />
      ) : customers.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table">
            <thead>
              <tr>
                <th>客户</th>
                <th>邮箱</th>
                <th>电话</th>
                <th>创建时间</th>
                <th className="table__actions-col">操作</th>
              </tr>
            </thead>
            <CustomerTableSkeleton rows={8} />
          </table>
        </div>
      ) : customerList.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="contact"
            description={debouncedQ !== '' ? '没有匹配的客户，换个关键词试试。' : '暂无客户。兑换授权码时客户会自动创建，也可点击右上角「登记客户」手动录入。'}
          />
        </div>
      ) : (
        <>
          <div className="table-frame">
            <table className="table">
              <thead>
                <tr>
                  <th>客户</th>
                  <th>邮箱</th>
                  <th>电话</th>
                  <th>创建时间</th>
                  <th className="table__actions-col">操作</th>
                </tr>
              </thead>
              <tbody>
                {customerList.map((customer) => (
                  <tr
                    key={customer.id}
                    className="table__row-clickable"
                    onClick={() => setViewingCustomerId(customer.id)}
                  >
                    <td>{customer.name ?? <span className="text-muted">未登记</span>}</td>
                    <td className="text-muted">{customer.email ?? '-'}</td>
                    <td className="text-muted">{customer.phone ?? '-'}</td>
                    <td>{formatDate(customer.created_at)}</td>
                    <td className="table__actions-col">
                      <span className="row-actions">
                        <button
                          type="button"
                          className="btn btn--quiet"
                          onClick={(e) => { e.stopPropagation(); setViewingCustomerId(customer.id); }}
                          title="查看详情"
                        >
                          <Icon name="eye" size={ICON_SIZE.inline} />
                          <span className="btn-label">查看</span>
                        </button>
                        {canWrite && (
                          <>
                            <button
                              type="button"
                              className="btn btn--quiet"
                              onClick={(e) => { e.stopPropagation(); setEditingCustomer(customer); }}
                              title="编辑"
                            >
                              <Icon name="pencil" size={ICON_SIZE.inline} />
                              <span className="btn-label">编辑</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn--quiet"
                              onClick={(e) => { e.stopPropagation(); setOrderingCustomer(customer); }}
                              title="登记订单"
                            >
                              <Icon name="plus" size={ICON_SIZE.inline} />
                              <span className="btn-label">登记订单</span>
                            </button>
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} limit={limit} total={total} hasMore={hasMore} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        </>
      )}

      {viewingCustomer !== null && (
        <CustomerDrawer
          customer={viewingCustomer}
          productList={products.state.kind === 'ready' ? products.state.data.items : []}
          canWrite={canWrite}
          onClose={() => setViewingCustomerId(null)}
          onEdit={() => setEditingCustomer(viewingCustomer)}
          onCreateOrder={() => setOrderingCustomer(viewingCustomer)}
        />
      )}

      {showCreateCustomer && (
        <CustomerDialog
          onClose={() => setShowCreateCustomer(false)}
          onSaved={() => { setShowCreateCustomer(false); refreshAll(); }}
        />
      )}
      {editingCustomer !== null && (
        <CustomerDialog
          initial={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSaved={() => { setEditingCustomer(null); refreshAll(); }}
        />
      )}
      {orderingCustomer !== null && (
        <OrderDialog
          customerId={orderingCustomer.id}
          customerName={orderingCustomer.name ?? orderingCustomer.email ?? orderingCustomer.id.slice(0, 8)}
          onClose={() => setOrderingCustomer(null)}
          onSaved={() => { setOrderingCustomer(null); refreshAll(); }}
        />
      )}
    </>
  );
}

/* ────────────────────────── 客户详情抽屉 ────────────────────────── */

function CustomerDrawer({
  customer,
  productList,
  canWrite,
  onClose,
  onEdit,
  onCreateOrder,
}: {
  customer: Customer;
  productList: readonly { id: string; name: string }[];
  canWrite: boolean;
  onClose: () => void;
  onEdit: () => void;
  onCreateOrder: () => void;
}) {
  const orders = useAsync(
    useCallback(() => listOrders(customer.id), [customer.id]),
    [customer.id],
  );

  const productName = (id: string): string => productList.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`${customer.name ?? '未登记'} 的客户详情`}>
        <div className="panel__head">
          <h2 className="panel__title">客户详情</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="panel__section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 className="panel__section-title" style={{ margin: 0 }}>基本信息</h3>
              {canWrite && (
                <button type="button" className="btn btn--quiet" onClick={onEdit}>
                  <Icon name="pencil" size={ICON_SIZE.inline} />
                  编辑
                </button>
              )}
            </div>
            <dl className="detail-list">
              <dt>姓名</dt>
              <dd>{customer.name ?? <span className="text-muted">未登记</span>}</dd>
              <dt>邮箱</dt>
              <dd>{customer.email ?? '-'}</dd>
              <dt>电话</dt>
              <dd>{customer.phone ?? '-'}</dd>
              <dt>备注</dt>
              <dd>{customer.note ?? '-'}</dd>
              <dt>创建时间</dt>
              <dd>{formatDate(customer.created_at)}</dd>
            </dl>
          </div>

          <div className="panel__section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 className="panel__section-title" style={{ margin: 0 }}>订单</h3>
              {canWrite && (
                <button type="button" className="btn btn--quiet" onClick={onCreateOrder}>
                  <Icon name="plus" size={ICON_SIZE.inline} />
                  登记订单
                </button>
              )}
            </div>
            {orders.state.kind === 'loading' ? (
              <p className="text-muted">加载中…</p>
            ) : orders.state.kind === 'failed' ? (
              <ErrorBlock message={describeError(orders.state.error, '加载订单')} onRetry={orders.reload} />
            ) : orders.state.data.items.length === 0 ? (
              <p className="text-muted">暂无订单。可点击「登记订单」手动录入。</p>
            ) : (
              <div className="table-frame" style={{ border: 'none', padding: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>订单号</th>
                      <th>产品</th>
                      <th>金额</th>
                      <th>来源</th>
                      <th>创建时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.state.data.items.map((order: Order) => (
                      <tr key={order.id}>
                        <td><code>{order.external_order_no ?? order.id.slice(0, 8)}</code></td>
                        <td>{productName(order.product_id)}</td>
                        <td>{order.amount_cents !== null ? `¥${(order.amount_cents / 100).toFixed(2)}` : '-'}</td>
                        <td className="text-muted">{order.source ?? '-'}</td>
                        <td>{formatDate(order.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── 登记 / 编辑客户对话框 ────────────────────────── */

function CustomerDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Customer;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = name.trim() !== '' || email.trim() !== '';

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: CustomerInput = {
        name: name.trim() === '' ? null : name.trim(),
        email: email.trim() === '' ? null : email.trim(),
        phone: phone.trim() === '' ? null : phone.trim(),
        note: note.trim() === '' ? null : note.trim(),
      };
      if (initial) await updateCustomer(initial.id, input);
      else await createCustomer(input);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title={initial ? '编辑客户' : '登记客户'} onClose={onClose} footer={
      <div className="dialog-actions">
        <button type="button" className="btn btn--quiet" onClick={onClose}>取消</button>
        <button type="button" className="btn btn--primary" disabled={!valid || submitting} onClick={handleSubmit}>
          {submitting ? '保存中…' : '保存'}
        </button>
      </div>
    }>
      <div className="field">
        <label>姓名</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="如：陆文琦" maxLength={120} />
      </div>
      <div className="field">
        <label>邮箱</label>
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="如：lu.wenqi@example.com" maxLength={254} />
      </div>
      <div className="field">
        <label>电话</label>
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
      </div>
      <div className="field">
        <label>备注</label>
        <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      </div>
      {!valid && <p className="field-hint">至少填写姓名或邮箱一项。</p>}
      {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
    </DialogShell>
  );
}

/* ────────────────────────── 登记订单对话框（source=manual） ────────────────────────── */

function OrderDialog({
  customerId,
  customerName,
  onClose,
  onSaved,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const products = useAsync(useCallback(() => listProducts({ limit: 200 }), []), []);
  const [productId, setProductId] = useState('');
  const [externalOrderNo, setExternalOrderNo] = useState('');
  const [amountYuan, setAmountYuan] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productList: readonly { id: string; name: string }[] =
    products.state.kind === 'ready' ? products.state.data.items : [];

  const handleSubmit = async () => {
    if (productId === '' || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const amount = amountYuan.trim() === '' ? null : Math.round(parseFloat(amountYuan) * 100);
      if (amount !== null && Number.isNaN(amount)) {
        setError('金额格式不正确');
        setSubmitting(false);
        return;
      }
      await createOrder({
        product_id: productId,
        customer_id: customerId,
        source: 'manual',
        external_order_no: externalOrderNo.trim() === '' ? null : externalOrderNo.trim(),
        amount_cents: amount,
        currency: amount === null ? null : 'CNY',
        note: note.trim() === '' ? null : note.trim(),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title={`登记订单 - ${customerName}`} onClose={onClose} footer={
      <div className="dialog-actions">
        <button type="button" className="btn btn--quiet" onClick={onClose}>取消</button>
        <button type="button" className="btn btn--primary" disabled={productId === '' || submitting} onClick={handleSubmit}>
          {submitting ? '保存中…' : '保存'}
        </button>
      </div>
    }>
      <div className="field">
        <label>产品（必填）</label>
        <select className="select" value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">选择产品…</option>
          {productList.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>外部订单号</label>
        <input className="input" value={externalOrderNo} onChange={(e) => setExternalOrderNo(e.target.value)} maxLength={120} placeholder="如：ORD-2026-1403" />
      </div>
      <div className="field">
        <label>金额（元，可选）</label>
        <input className="input" type="number" min="0" step="0.01" value={amountYuan} onChange={(e) => setAmountYuan(e.target.value)} placeholder="如：3999.00" />
      </div>
      <div className="field">
        <label>备注</label>
        <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      </div>
      {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
    </DialogShell>
  );
}

/* ────────────────────────── 对话框壳 ────────────────────────── */

function DialogShell({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div
        className="panel panel--form"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="panel__head">
          <h2 className="panel__title">{title}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
}

function CustomerTableSkeleton({ rows }: { rows: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>
          <td><div className="skeleton-bar" style={{ width: '55%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '70%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '50%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '40%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '60%' }} /></td>
        </tr>
      ))}
    </tbody>
  );
}
